// packages/common-ui/src/utils/homepageDataLayerService.ts
import { detectHomepageAnalytics } from './analyticsDetection';
import { adobeDataLayer } from './AdobeDataLayer';
import { CmsContentTypeId } from 'tsconfig/types/Contentful.types';
import type { DLClickEventHandlerProps } from 'tsconfig/types';

export interface HomepageClickEventProps {
  contentItem: Record<string, unknown>;
  layoutIndex?: number;
  componentType?: CmsContentTypeId;
  data: string;
  additionalProps?: {
    position?: number;
    columnTitle?: string;
  };
}

export interface HomepageDataLayerConfig {
  renderInPageLevel?: boolean | ((contentType?: CmsContentTypeId) => boolean);
  fallbackClickHandler?: DLClickEventHandlerProps;
}

type ClickEventHandler = (data: string, additionalProps?: { position?: number; columnTitle?: string }) => void;
type CarouselClickEventHandler = (data: string, position: number) => void;
type ColumnClickEventHandler = (data: string) => void;

/**
 * Service class to handle homepage analytics data layer events
 * Extracted from RenderDynamicComponent to separate concerns
 */
export class HomepageDataLayerService {
  private config: HomepageDataLayerConfig;

  constructor(config: HomepageDataLayerConfig = {}) {
    this.config = config;
  }

  /**
   * Helper function for column title detection (simplified version for columns)
   */
  private getColumnTitle(columnEntryTitle?: string): string {
    if (!columnEntryTitle) {
      return 'unknown';
    }
    const analyticsResult = detectHomepageAnalytics({
      contentItem: { fields: { entryTitle: columnEntryTitle } },
      layoutIndex: undefined,
      componentType: 'Component-Column',
    });
    return analyticsResult?.sectionName || 'unknown';
  }

  /**
   * Handles click events for homepage components with analytics detection
   */
  handleHomepageClickEvent(props: HomepageClickEventProps): void {
    const { contentItem, layoutIndex, componentType, data, additionalProps = {} } = props;

    const pageInfo = adobeDataLayer.getPageInfo();
    const page = pageInfo.pageName === 'homepage' ? 'home' : pageInfo.pageName;

    const { renderInPageLevel, fallbackClickHandler } = this.config;
    const shouldTrackInPageLevel =
      typeof renderInPageLevel === 'function' ? renderInPageLevel(componentType) : renderInPageLevel;

    if (shouldTrackInPageLevel && componentType) {
      // Use hybrid analytics detection
      const analyticsResult = detectHomepageAnalytics({
        contentItem,
        layoutIndex,
        componentType,
      });

      const { sectionName: section, eventType } = analyticsResult || {
        sectionName: 'unknown',
        eventType: 'homeSectionBanner' as const,
      };

      const clickName = section === 'hero banner' ? 'hero banner click' : 'home sections click';
      const position = additionalProps.position && section === 'hero banner' ? ` ${additionalProps.position}` : '';

      // Log detection method in development
      if (process.env.NODE_ENV === 'development' && analyticsResult) {
        // eslint-disable-next-line no-console
        console.log('[Homepage Analytics]', {
          section,
          eventType,
          method: analyticsResult.detectionMethod,
          confidence: analyticsResult.confidence,
          ruleId: analyticsResult.metadata?.ruleId,
          entryTitle: (contentItem?.fields as Record<string, unknown>)?.entryTitle,
        });
      }

      // Handle different component types
      this.trackComponentClick({
        componentType,
        eventType,
        page,
        section,
        position,
        data,
        clickName,
        additionalProps,
      });
    } else {
      // Fallback to provided click handler
      fallbackClickHandler?.(data, componentType);
    }
  }

  /**
   * Tracks click events based on component type
   */
  private trackComponentClick({
    componentType,
    eventType,
    page,
    section,
    position,
    data,
    clickName,
    additionalProps,
  }: {
    componentType: CmsContentTypeId;
    eventType: 'homeHeroBanner' | 'homeSectionBanner';
    page: string;
    section: string;
    position: string;
    data: string;
    clickName: string;
    additionalProps: { position?: number; columnTitle?: string };
  }): void {
    if (componentType === CmsContentTypeId.Carousel) {
      adobeDataLayer.clickEvent(eventType, {
        clickDescription: `${page}:${section}${position}:${data}`,
        clickName,
        clickLocation: `${section}${position}`,
        clickText: data,
        clickAction: 'navigation',
      });
    } else {
      const sectionName =
        additionalProps?.columnTitle && additionalProps?.columnTitle !== 'unknown'
          ? additionalProps.columnTitle
          : section;
      adobeDataLayer.clickEvent(eventType, {
        clickDescription: `${page}:${sectionName}${position}:${data}`,
        clickName,
        clickLocation: `${sectionName}${position}`,
        clickText: data,
        clickAction: 'navigation',
      });
    }
  }

  /**
   * Creates a standard click event handler that matches DLClickEventHandlerProps interface
   */
  createStandardClickEventHandler(
    contentItem: Record<string, unknown>,
    layoutIndex?: number,
    componentType?: CmsContentTypeId,
  ): DLClickEventHandlerProps {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return (data: string, clickType?: string): void => {
      // For standard handlers, we don't use clickType but maintain interface compatibility
      this.handleHomepageClickEvent({
        contentItem,
        layoutIndex,
        componentType,
        data,
        additionalProps: {},
      });
    };
  }

  /**
   * Creates a click event handler function for a specific component type
   */
  createClickEventHandler(
    contentItem: Record<string, unknown>,
    layoutIndex?: number,
    componentType?: CmsContentTypeId,
  ): ClickEventHandler {
    return (data: string, additionalProps: { position?: number; columnTitle?: string } = {}): void => {
      this.handleHomepageClickEvent({
        contentItem,
        layoutIndex,
        componentType,
        data,
        additionalProps,
      });
    };
  }

  /**
   * Creates a click event handler for carousel components with position tracking
   */
  createCarouselClickEventHandler(
    contentItem: Record<string, unknown>,
    layoutIndex?: number,
  ): CarouselClickEventHandler {
    return (data: string, position: number): void => {
      this.handleHomepageClickEvent({
        contentItem,
        layoutIndex,
        componentType: CmsContentTypeId.Carousel,
        data,
        additionalProps: { position },
      });
    };
  }

  /**
   * Creates a click event handler for column components with column title detection
   */
  createColumnClickEventHandler(
    contentItem: Record<string, unknown>,
    columnContent: Record<string, unknown>,
  ): ColumnClickEventHandler {
    const columnTitle = this.getColumnTitle((columnContent?.fields as Record<string, unknown>)?.entryTitle as string);
    return (data: string): void => {
      this.handleHomepageClickEvent({
        contentItem,
        layoutIndex: undefined,
        componentType: CmsContentTypeId.Column,
        data,
        additionalProps: { columnTitle },
      });
    };
  }

  /**
   * Creates a click event handler for container components
   */
  createContainerEventHandler(
    contentItem: Record<string, unknown>,
    entryTitle: string,
  ): (data: string, contentType?: string) => void {
    return (data: string, contentType?: string): void => {
      if (contentType === CmsContentTypeId.Link) {
        const linkHandler = this.createStandardClickEventHandler(
          contentItem,
          undefined,
          contentType as CmsContentTypeId,
        );
        linkHandler(`${entryTitle} - ${data}`);
      } else {
        const standardHandler = this.createStandardClickEventHandler(
          contentItem,
          undefined,
          contentType as CmsContentTypeId,
        );
        standardHandler(data);
      }
    };
  }

  /**
   * Updates the service configuration
   */
  updateConfig(config: Partial<HomepageDataLayerConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

/**
 * Factory function to create a homepage data layer service with configuration
 */
export const createHomepageDataLayerService = (config: HomepageDataLayerConfig): HomepageDataLayerService =>
  new HomepageDataLayerService(config);
